// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import {
  canChangeRole,
  canToggleDisabled,
  changedText,
  draftFor,
  isSelf,
  resetSentText,
  roleCounts,
  SEARCH_DEBOUNCE_MS,
  sortUsers,
  userEditCheck,
  userErrorText,
  userName,
  type UserAction,
  type UserDraft,
} from '../admin/model';
import { authApi, type UserPatch } from '../api/auth';
import type { AuthUser, GlobalRole } from '../api/types';
import { GLOBAL_ROLE_HINTS, GLOBAL_ROLE_LABELS, GLOBAL_ROLES } from '../app/auth';
import { useAuth } from '../app/AuthGate';
import { useArmedConfirm } from '../app/useArmedConfirm';
import { useAsync } from '../app/useAsync';
import { useMutationQueue } from '../app/useMutationQueue';
import { RoleSummary } from '../components/admin/RoleSummary';
import { DataTable, type Column } from '../components/DataTable';
import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { Select } from '../components/Select';
import { Spinner } from '../components/Spinner';
import { SearchInput, TextInput } from '../components/TextInput';
import styles from './AdminUsersPage.module.css';

/**
 * Brugere — every account on the server, for administrators (ruxd-wmc.6,
 * frontend-rn0). Accounts are Supabase's: ruxd changes role, name, e-mail and
 * ban through GoTrue's admin API, and sends Supabase's password-recovery
 * mail. New accounts are made in Supabase, not here.
 */
export function AdminUsersPage() {
  const auth = useAuth();
  const me = auth?.me;
  let body;
  if (me?.mode !== 'server')
    body = (
      <section className={styles.panel} aria-label="Brugere">
        <p className={styles.text}>
          Serveren kører i lokal tilstand: én person, intet login og ingen brugere at styre. Start ruxd uden{' '}
          <code className={styles.code}>--local</code> for brugere og roller.
        </p>
      </section>
    );
  else if (me.user.role !== 'admin')
    body = (
      <section className={styles.panel} aria-label="Brugere">
        <p className={styles.text}>
          Kun administratorer kan se og ændre brugere. Spørg en administrator, hvis din rolle skal ændres.
        </p>
      </section>
    );
  else body = <UsersPanel myId={me.user.id} />;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Brugere</h1>
        <span className={styles.sub}>Konti, roller og spærringer på serveren</span>
      </header>
      {body}
    </div>
  );
}

type Result = { ok: boolean; text: string } | null;

/** The armed confirm's key: which account, and which of its two confirmed actions. */
type Armed = `${'reset' | 'ban'}:${string}`;

function UsersPanel({ myId }: { myId: string }) {
  const headingId = useId();
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const users = useAsync<AuthUser[]>((s) => authApi.users(query, s), [query]);
  const [editing, setEditing] = useState<{ id: string; draft: UserDraft } | null>(null);
  const [result, setResult] = useState<Result>(null);
  const action = useRef<UserAction>('edit');

  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    ignoreCaseRole: true,
    onError: (cause) => setResult({ ok: false, text: userErrorText(cause, action.current) }),
    onSettled: () => users.reload(),
  });
  const confirm = useArmedConfirm<Armed>(busy, users.data);

  if (users.error && !users.data)
    return (
      <section className={styles.panel}>
        <p className={styles.error} role="alert">
          {userErrorText(users.error, 'load')}
        </p>
        <div>
          <Button variant="outline" size="sm" onClick={users.reload}>
            Prøv igen
          </Button>
        </div>
      </section>
    );
  if (!users.data) return <Spinner label="Indlæser brugere…" />;

  const list = sortUsers(users.data);

  const run = (kind: UserAction, work: () => Promise<string>) => {
    setResult(null);
    confirm.disarm();
    action.current = kind;
    void mutate(async () => {
      setResult({ ok: true, text: await work() });
    });
  };

  const patch = (user: AuthUser, kind: 'edit' | 'role' | 'disable' | 'enable', body: UserPatch) =>
    run(kind, async () => {
      const updated = await authApi.patchUser(user.id, body);
      if (kind === 'edit') setEditing(null);
      return changedText(updated, kind);
    });

  const saveEdit = (e: FormEvent, user: AuthUser) => {
    e.preventDefault();
    if (!editing || busy) return;
    const check = userEditCheck(editing.draft, user);
    if (!check.ok) {
      setResult({ ok: false, text: check.reason });
      return;
    }
    patch(user, 'edit', check.value);
  };

  const startEdit = (user: AuthUser) => {
    setResult(null);
    confirm.disarm();
    setEditing({ id: user.id, draft: draftFor(user) });
  };

  const cancelEdit = () => {
    setResult(null);
    setEditing(null);
  };

  /** Arming a confirm clears the last result line, so an old error is not read as this action's. */
  const arm = (key: Armed, button: HTMLElement) => {
    setResult(null);
    confirm.arm(key, button);
  };

  const setDraft = (next: Partial<UserDraft>) =>
    setEditing((cur) => (cur ? { ...cur, draft: { ...cur.draft, ...next } } : cur));

  const isEditing = (u: AuthUser) => editing?.id === u.id;
  const formId = (u: AuthUser) => `${headingId}-edit-${u.id}`;

  const columns: Column<AuthUser>[] = [
    {
      key: 'name',
      header: 'Navn',
      render: (u) =>
        isEditing(u) && editing ? (
          <TextInput
            variant="outline"
            size="sm"
            className={styles.cellInput}
            form={formId(u)}
            value={editing.draft.username}
            placeholder="Intet navn"
            aria-label={`Navn for ${u.email}`}
            autoComplete="off"
            autoFocus
            onChange={(e) => setDraft({ username: e.target.value })}
          />
        ) : (
          <span className={styles.name}>
            {u.display_name || <span className={styles.none}>—</span>}
            {isSelf(u, myId) && <span className={styles.you}>dig</span>}
          </span>
        ),
    },
    {
      key: 'email',
      header: 'E-mail',
      render: (u) =>
        isEditing(u) && editing ? (
          <TextInput
            variant="outline"
            size="sm"
            mono
            className={styles.cellInput}
            form={formId(u)}
            type="email"
            value={editing.draft.email}
            aria-label={`E-mail for ${userName(u)}`}
            autoComplete="off"
            onChange={(e) => setDraft({ email: e.target.value })}
          />
        ) : (
          <span className="mono">{u.email}</span>
        ),
    },
    {
      key: 'role',
      header: 'Rolle',
      render: (u) => {
        const locked = !canChangeRole(u, myId);
        return (
          <Select
            variant="outline"
            size="sm"
            value={u.role}
            disabled={busy || locked}
            title={locked ? 'Du kan ikke ændre din egen rolle' : GLOBAL_ROLE_HINTS[u.role]}
            aria-label={`Rolle for ${userName(u)}`}
            onChange={(e) => patch(u, 'role', { role: e.target.value as GlobalRole })}
          >
            {GLOBAL_ROLES.map((r) => (
              <option key={r} value={r}>
                {GLOBAL_ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) =>
        u.disabled ? (
          <Pill tone="crit" title="Kontoen er spærret i Supabase og kan ikke logge ind">
            Spærret
          </Pill>
        ) : (
          <Pill tone="good">Aktiv</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <div className={styles.actions}>{isEditing(u) ? editActions(u) : rowActions(u)}</div>
      ),
    },
  ];

  function editActions(u: AuthUser) {
    return (
      <form id={formId(u)} className={styles.actions} onSubmit={(e) => saveEdit(e, u)}>
        <Button type="submit" variant="primary" size="sm" disabled={busy}>
          Gem
        </Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={cancelEdit}>
          Annuller
        </Button>
      </form>
    );
  }

  function rowActions(u: AuthUser) {
    const name = userName(u);
    const resetKey: Armed = `reset:${u.id}`;
    const banKey: Armed = `ban:${u.id}`;
    return (
      <>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => startEdit(u)}>
          Rediger
        </Button>
        {confirm.armed === resetKey ? (
          <Button
            variant="primary"
            size="sm"
            className={styles.resetBtn}
            disabled={busy}
            aria-label={`Send nulstilling af adgangskode til ${u.email}`}
            title={`Send en mail til ${u.email}`}
            onClick={() => run('reset', () => authApi.sendPasswordReset(u.id).then(() => resetSentText(u)))}
            onBlur={confirm.disarm}
          >
            Ja, send mail
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={styles.resetBtn}
            disabled={busy || u.disabled}
            title={u.disabled ? 'Kontoen er spærret' : 'Send nulstilling af adgangskode'}
            onClick={(e) => arm(resetKey, e.currentTarget)}
          >
            Nulstil adgangskode
          </Button>
        )}
        {confirm.armed === banKey ? (
          <Button
            variant={u.disabled ? 'primary' : 'outline'}
            tone={u.disabled ? 'neutral' : 'danger'}
            size="sm"
            armed
            className={styles.banBtn}
            disabled={busy}
            aria-label={u.disabled ? `Genåbn ${name}` : `Spær ${name}`}
            onClick={() => patch(u, u.disabled ? 'enable' : 'disable', { disabled: !u.disabled })}
            onBlur={confirm.disarm}
          >
            {u.disabled ? 'Ja, genåbn' : 'Ja, spær'}
          </Button>
        ) : (
          <Button
            variant="outline"
            tone={u.disabled ? 'neutral' : 'danger'}
            size="sm"
            className={styles.banBtn}
            disabled={busy || !canToggleDisabled(u, myId)}
            title={canToggleDisabled(u, myId) ? undefined : 'Du kan ikke spærre din egen konto'}
            onClick={(e) => arm(banKey, e.currentTarget)}
          >
            {u.disabled ? 'Genåbn' : 'Spær'}
          </Button>
        )}
      </>
    );
  }

  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <div className={styles.panelHead}>
        <h2 id={headingId} className={styles.panelHeading}>
          Konti
        </h2>
        <span className={styles.count}>{list.length}</span>
        {users.loading && <span className={styles.loading}>Søger…</span>}
        <label className={styles.search}>
          <span className={styles.srOnly}>Søg i brugere</span>
          <SearchInput
            variant="outline"
            size="md"
            className={styles.searchInput}
            value={search}
            placeholder="Søg på navn eller e-mail"
            autoComplete="off"
            onChange={(e) => {
              setSearch(e.target.value);
              setResult(null);
            }}
          />
        </label>
      </div>
      <p className={styles.muted}>
        Konti ligger i Supabase, og nye brugere oprettes dér. Her ændrer du rolle, navn og e-mail, sender en mail til
        nulstilling af adgangskode og spærrer konti. En spærret konto kan ikke logge ind.
      </p>

      <RoleSummary roles={GLOBAL_ROLES} labels={GLOBAL_ROLE_LABELS} hints={GLOBAL_ROLE_HINTS} counts={roleCounts(list)} />

      <DataTable
        columns={columns}
        rows={list}
        rowKey={(u) => u.id}
        empty={
          <p className={styles.muted}>
            {query ? `Ingen brugere matcher »${query}«.` : 'Der er ingen brugere endnu.'}
          </p>
        }
      />

      {result &&
        (result.ok ? (
          <p className={styles.ok} role="status">
            {result.text}
          </p>
        ) : (
          <p className={styles.error} role="alert">
            {result.text}
          </p>
        ))}
    </section>
  );
}

/** `value`, once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}
